import style from "@/styles/pages/PurchasePrice/PurchasePrice.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";
import CurrencyInput from "react-currency-input-field";
import { setMortgageBalance } from "@/slices/propertyInfoSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { setApplicationProgress } from "@/slices/applicationSlice";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { applicationCategory } from "@/types/applicationType";

export default function MortgageBalance() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [balance, setBalance] = useState("");

    const propertyInfo = useSelector(
        (state: RootState) => state.propertyInfo.propertyInfo
    );
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            mortgageBalance: ""
        },
    });

    useEffect(() => {
        document.title = "Enter Mortgage Balance";
        methods.setValue("mortgageBalance", propertyInfo ? propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString() : "");
        setBalance(propertyInfo.mortgageBalance == 0 ? "" : propertyInfo.mortgageBalance.toString());

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.propertyInfo = propertyInfo;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });

        dispatch(setApplicationProgress(3));
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        dispatch(setMortgageBalance(+balance));
        setClickInsightApplicationProgress('Your new mortgage - What is your approximate current mortgage balance?');
        application.applicationCategory == applicationCategory.Refinance ? navigate("/single/loan-amount") : navigate("/single/mortgage-term");
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">What is your approximate current mortgage balance?</h1>
                        <p className="sub-heading">This is the approximate amount you still owe on your current mortgage.</p>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="flex md:flex-row flex-col w-max">
                                        <div className="mt-5">
                                            <label htmlFor="propertyValue">Current Balance</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.mortgageBalance?.message
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <CurrencyInput
                                                    id="propertyValue"
                                                    placeholder="$"
                                                    prefix={"$"}
                                                    decimalsLimit={2}
                                                    decimalScale={2}
                                                    allowNegativeValue={false}
                                                    value={balance}
                                                    maxLength={10}
                                                    className={clsx(
                                                        methods.formState?.errors?.mortgageBalance?.message ? "error" : "",
                                                        "mt-2 w:[220px] md:w-[222px]"
                                                    )}
                                                    {...methods.register("mortgageBalance", {
                                                        required: "Enter the approximate remaining balance of your mortgage.",
                                                        validate: {
                                                            mortgageBalance: (c) => {
                                                                let currentAmount = c.replaceAll("$", "").replaceAll(",", "");
                                                                if (+currentAmount > 0) {
                                                                    return true
                                                                } else {
                                                                    return "The remaining balance cannot be zero."
                                                                }
                                                            },
                                                        },
                                                    })}
                                                    onValueChange={(value, name, values) => value != undefined ? setBalance(value) : setBalance("")}
                                                />
                                            </div>
                                            {methods.formState?.errors?.mortgageBalance?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.mortgageBalance?.message.toString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}